import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/shared/api/httpClient';
import './LoginPage.css';

function defaultRouteForRole(role: string): string {
  return role === 'SystemAdmin' || role === 'EventOrganiser' ? '/admin/events' : '/dashboard';
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const auth = await login(email.trim(), password, rememberDevice);
      const target = (location.state as { from?: string } | null)?.from ?? defaultRouteForRole(auth.role);
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Unable to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <img src="/brand/logo-alt.png" alt="The Foot & Ankle Show" className="login-page__logo" />
      <div className="login-page__card">
        <h1 className="login-page__title font-display">Sign in</h1>
        <p className="login-page__subtitle">Scan badges. Capture leads. Even offline.</p>

        <form className="login-page__form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="error-banner" role="alert">
              {error}
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="field__input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="field__input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <label className="login-page__remember">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Remember this device for 30 days
          </label>

          <Button type="submit" size="large" fullWidth isLoading={isSubmitting}>
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}